// Style Hub (Connections, Style Master, Material Master) and the S&P ledger column menu (SPLedgerHeader) — the
// screens' strings in the portal's four languages (2026-10-21). The Style Master column headings are NOT here on
// purpose: they are SAP's own field labels (DD04T.REPTEXT) and stay as SAP prints them.
const en = {
  tabs: {
    connections: 'Connections',
    style: 'Style Master',
    material: 'Material Master',
  },
  state: {
    connected: 'Connected',
    failed: 'Failed',
    notConfigured: 'Not configured',
    scheduler: 'Runs on the scheduler',
    upToDate: 'Up to date',
    lastRunFailed: 'Last run failed',
    stale: 'Stale',
  },
  conn: {
    transport: 'Transport',
    host: 'Host',
    account: 'Account',
    roundTrip: 'Round trip',
    waitingFor: 'Waiting for: {{keys}}',
    scheduled: 'Refreshed twice a day by the scheduled job, not from this server.',
  },
  age: {
    min: '{{n}} min ago',
    hours: '{{n}} h ago',
    days: '{{n}} days ago',
  },
  refresh: {
    never: 'never',
    result: 'read {{read}} · +{{inserted}} new · {{updated}} changed',
    failed: 'failed',
    head: 'Scheduled refresh',
    sapNow: 'Refresh SAP now',
    nsNow: 'Refresh NetSuite now',
    stepRunning: '{{step}}...',
    checking: 'Checking...',
    checkAgain: 'Check again',
    note: 'The three ledgers are reloaded from SAP and NetSuite on a schedule. This is when each one last succeeded — not when this page was opened. Refreshing by hand does the same work as the scheduled job; if the job happens to be running it will say so rather than run alongside it.',
    done: 'Refreshed — {{done}}',
    colLedger: 'Ledger',
    colRows: 'Rows',
    colLast: 'Last refreshed',
    colResult: 'Result',
    colState: 'State',
    colProblem: 'Problem',
    stepSapPo: 'SAP PO lines',
    stepSapSo: 'SAP SO lines',
    stepNsPo: 'NetSuite PO lines',
    needsEdit: 'Refreshing writes to the ledger and needs edit rights on the Hub.',
    summary: '{{label}}: +{{added}} new, {{changed}} changed',
  },
  failedStatus: 'Failed ({{status}})',
  src: {
    head: 'Source connections',
    lastChecked: 'Last checked {{time}}',
    testing: 'Testing...',
    testAgain: 'Test again',
    noCreds: 'This server holds no integration credentials, so it cannot test a connection from here — by design, so that one machine holds the secrets and one path writes the ledgers. The data is not stale: the scheduled refresh runs from the staging backend and the table underneath shows when each ledger last succeeded. The manual import buttons on this page will not work here.',
    plmNote: '<b>Centric PLM is not listed.</b> Its REST API is licensed and switched off, so there is no connection to test. Style names come from SAP (<c>MAKT</c>); Season comes from the PLM export file imported on the <b>Style Master</b> tab, and from the PLM → SAP interface for Season 2027 onward.',
  },
  style: {
    head: 'Style backbone',
    note: "Every column comes from SAP and carries SAP's own column heading. <c>MATNR</c>, <c>FERTH</c>, <c>MAKTX</c>, <c>MTART</c>, <c>MATKL</c>, <c>ZZCOLOR</c> and <c>EAN11</c> are re-read over RFC twice a day, at 05:30 and 13:30. The two <c>PRDHA</c> columns are not on that schedule — they are rewritten only when someone runs the style enrichment, so they can lag the others. <b>Season</b> is the one column that is not SAP at all: it comes from the PLM export. <b>Douzone material</b> is SAP's name for the pre-SAP (Douzone) material number, the one used here as the selling SKU. Searching also reaches the BOM crosswalk, so an SFP or component code finds its style even though neither has a column of its own.",
    noSeason: 'No season yet',
    countN: '{{n}} styles',
    countOf: '{{n}} of {{total}} styles',
    noMatch: 'No styles match',
    importHead: 'Import PLM season',
    importNote1: "For the seasons PLM will <b>not</b> send through SAP. From Season 2027 the Season arrives on the SAP interface (Hyun Kim, 2026-08-26); everything earlier is a closed season whose value will never change again, so a one-time export from PLM is enough for it. Drop that export here — <c>.xlsx</c> or <c>.csv</c>, straight out of PLM's list view, no reshaping.",
    importNote2: 'Nothing about the layout matters: the code column and the season column are found by looking at the values, not the headers, and the preview says which two it picked. A blank season is never written, so a file that omits a style cannot erase one.',
    reading: 'Reading...',
    check: 'Check the file',
    apply_one: 'Apply {{count}} change',
    apply_other: 'Apply {{count}} changes',
    readError: 'Could not read the file',
    applied: 'Applied — {{n}} styles updated from {{file}}.',
    read: 'Read {{n}} rows from {{file}}. Code column "{{code}}", season column "{{season}}".',
    statWould: 'Would change',
    statCorrect: 'Already correct',
    statNotFound: 'Code not found',
    statBlank: 'Blank season',
    statMulti: 'In several line-ups',
    statMultiSub: 'latest season kept',
    colSap: 'SAP Code',
    colNow: 'Season now',
    colFile: 'Season in the file',
    notFound: 'Not found here, first few: <c>{{list}}</c>',
    multi: 'In several line-ups, first few: <c>{{list}}</c> — the latest season is the one written.',
    expTitle: 'Style Hub — Style Master (SAP live, except Season)',
    expSheet: 'Style Master',
  },
  common: {
    rowNo: 'No',
    exportExcel: 'Export Excel',
    building: 'Building…',
    clearTools: 'Clear sort & filters',
    refresh: 'Refresh',
    saving: 'Saving…',
    rowsN: '{{n}} rows',
    rowsOf: '{{n}} of {{total}} rows',
    search: 'Search: {{q}}',
    exportFailed: 'Export failed ({{status}})',
  },
  season: {
    head: 'Season & Buy#',
    note1: 'Both fields are entered by a person and no import ever writes them. Entry is per <b>document</b>, not per line: there are 29,937 PO lines but only 2,738 purchase orders, and an order belongs to one season and one buy anyway. Every edit is recorded in Change Log with the value before and after.',
    note2: "<b>PLM season</b> is shown for reference only — it is what PLM says about the styles on the order, never written automatically. It can legitimately differ from what belongs here: PO 4500003037 was issued in August 2026 for a Season 2025 style, because a re-order keeps the original season. Where an order's lines resolve to more than one PLM season, both are shown.",
    sapPo: 'SAP PO',
    nsPo: 'NetSuite PO',
    allYears: 'All years',
    searchPh: 'PO number or supplier…',
    missing: 'Missing Season or Buy#',
    docs: '{{n}} documents',
    seasonPh: 'Season, e.g. S26',
    buyPh: 'Buy#, e.g. Buy#2 SLT',
    applyTo: 'Apply to {{n}} selected',
    colDoc: 'Document',
    colDate: 'Date',
    colSupplier: 'Supplier',
    colLines: 'Lines',
    colSeason: 'Season ✎',
    colBuy: 'Buy# ✎',
    colPlm: 'PLM season',
    editTitle: 'Click to edit — applies to every line on this document',
    mixed: 'mixed: {{a}} / {{b}}',
    none: 'No documents',
    typeFirst: 'Type a Season or a Buy# first',
    done: '{{docs}} document(s), {{lines}} lines, {{logged}} change-log entries',
  },
  material: {
    head: 'SAP Material Master',
    snapshot: 'Snapshot of <c>{{file}}</c>, loaded {{date}} — not a live SAP feed.',
    searchPh: 'SAP code, name, legacy Item#, PLM no. or barcode',
    allTypes: 'All types',
    noMatch: 'No rows match',
    expTitle: 'Style Hub — SAP Material Master',
    expSheet: 'Material Master',
    typeFilter: 'Type: {{type}}',
    col: {
      sapCode: 'SAP Code',
      type: 'Type',
      nameEn: 'Name (EN)',
      lineCategory: 'Line / Category',
      legacy: 'Legacy Item#',
      plm: 'PLM No.',
      uom: 'UoM',
      barcode: 'Barcode',
    },
  },
  ledger: {
    sortAZ: 'Sort A → Z',
    sortZA: 'Sort Z → A',
    searchCol: 'Search {{label}}…',
    loadingValues: 'Loading values…',
    selectAll: '(Select all)',
    noMatches: 'No matches',
    truncated: 'Showing the first 1,000 values — narrow with the search box.',
    clear: 'Clear',
    cancel: 'Cancel',
    ok: 'OK',
    clickSort: '{{label}} — click to sort',
    filteredTo: 'Filtered to {{n}} value(s)',
    sortFilter: 'Sort and filter',
  },
};

const ko = {
  tabs: {
    connections: '연결',
    style: '스타일 마스터',
    material: '자재 마스터',
  },
  state: {
    connected: '연결됨',
    failed: '실패',
    notConfigured: '설정 안 됨',
    scheduler: '스케줄러에서 실행',
    upToDate: '최신',
    lastRunFailed: '마지막 실행 실패',
    stale: '오래됨',
  },
  conn: {
    transport: '전송 방식',
    host: '호스트',
    account: '계정',
    roundTrip: '왕복 시간',
    waitingFor: '대기 중: {{keys}}',
    scheduled: '이 서버가 아니라 예약 작업이 하루 두 번 새로고침합니다.',
  },
  age: {
    min: '{{n}}분 전',
    hours: '{{n}}시간 전',
    days: '{{n}}일 전',
  },
  refresh: {
    never: '없음',
    result: '읽음 {{read}} · 신규 +{{inserted}} · 변경 {{updated}}',
    failed: '실패',
    head: '예약 새로고침',
    sapNow: '지금 SAP 새로고침',
    nsNow: '지금 NetSuite 새로고침',
    stepRunning: '{{step}}...',
    checking: '확인 중...',
    checkAgain: '다시 확인',
    note: '세 원장은 일정에 따라 SAP와 NetSuite에서 다시 불러옵니다. 여기 표시되는 것은 각 원장이 마지막으로 성공한 시각이며, 이 페이지를 연 시각이 아닙니다. 수동 새로고침은 예약 작업과 같은 일을 하며, 작업이 이미 실행 중이면 함께 실행하지 않고 그렇다고 알려 줍니다.',
    done: '새로고침 완료 — {{done}}',
    colLedger: '원장',
    colRows: '행 수',
    colLast: '마지막 새로고침',
    colResult: '결과',
    colState: '상태',
    colProblem: '문제',
    stepSapPo: 'SAP PO 라인',
    stepSapSo: 'SAP SO 라인',
    stepNsPo: 'NetSuite PO 라인',
    needsEdit: '새로고침은 원장에 기록하므로 Hub 편집 권한이 필요합니다.',
    summary: '{{label}}: 신규 +{{added}}, 변경 {{changed}}',
  },
  failedStatus: '실패 ({{status}})',
  src: {
    head: '소스 연결',
    lastChecked: '마지막 확인 {{time}}',
    testing: '테스트 중...',
    testAgain: '다시 테스트',
    noCreds: '이 서버에는 연동 자격 증명이 없어 여기서는 연결을 테스트할 수 없습니다 — 비밀 정보는 한 서버에만 두고 원장은 한 경로로만 기록하도록 한 설계입니다. 데이터가 오래된 것은 아닙니다: 예약 새로고침은 스테이징 백엔드에서 실행되며, 아래 표에 각 원장이 마지막으로 성공한 시각이 나옵니다. 이 페이지의 수동 가져오기 버튼은 여기서 작동하지 않습니다.',
    plmNote: '<b>Centric PLM은 목록에 없습니다.</b> REST API가 라이선스 제품이고 꺼져 있어 테스트할 연결이 없습니다. 스타일명은 SAP(<c>MAKT</c>)에서 오고, 시즌은 <b>스타일 마스터</b> 탭에서 가져온 PLM 내보내기 파일과, 2027 시즌부터는 PLM → SAP 인터페이스에서 옵니다.',
  },
  style: {
    head: '스타일 기본 정보',
    note: '모든 열은 SAP에서 오며 SAP 자체의 열 제목을 그대로 씁니다. <c>MATNR</c>, <c>FERTH</c>, <c>MAKTX</c>, <c>MTART</c>, <c>MATKL</c>, <c>ZZCOLOR</c>, <c>EAN11</c>은 하루 두 번(05:30, 13:30) RFC로 다시 읽습니다. 두 <c>PRDHA</c> 열은 이 일정에 포함되지 않습니다 — 누군가 스타일 보강 작업을 실행할 때만 다시 기록되므로 다른 열보다 늦을 수 있습니다. <b>Season</b>은 SAP가 아닌 유일한 열로, PLM 내보내기에서 옵니다. <b>Douzone material</b>은 SAP 이전(더존) 자재번호를 SAP에서 부르는 이름이며, 여기서는 판매 SKU로 쓰입니다. 검색은 BOM 크로스워크까지 닿으므로, SFP나 부품 코드는 자체 열이 없어도 해당 스타일을 찾습니다.',
    noSeason: '시즌 없음',
    countN: '스타일 {{n}}개',
    countOf: '스타일 {{total}}개 중 {{n}}개',
    noMatch: '일치하는 스타일이 없습니다',
    importHead: 'PLM 시즌 가져오기',
    importNote1: 'PLM이 SAP를 통해 보내지 <b>않는</b> 시즌용입니다. 2027 시즌부터는 시즌이 SAP 인터페이스로 들어옵니다(Hyun Kim, 2026-08-26). 그 이전은 값이 다시 바뀌지 않는 마감된 시즌이므로 PLM에서 한 번 내보내면 충분합니다. 그 파일을 여기에 놓으세요 — PLM 목록 화면에서 바로 내보낸 <c>.xlsx</c> 또는 <c>.csv</c>, 형태를 바꿀 필요는 없습니다.',
    importNote2: '레이아웃은 상관없습니다: 코드 열과 시즌 열은 헤더가 아니라 값을 보고 찾으며, 미리보기에서 어떤 두 열을 골랐는지 알려 줍니다. 빈 시즌은 절대 기록되지 않으므로, 어떤 스타일이 빠진 파일이 기존 시즌을 지울 수 없습니다.',
    reading: '읽는 중...',
    check: '파일 확인',
    apply_one: '변경 {{count}}건 적용',
    apply_other: '변경 {{count}}건 적용',
    readError: '파일을 읽을 수 없습니다',
    applied: '적용 완료 — {{file}}에서 스타일 {{n}}개를 업데이트했습니다.',
    read: '{{file}}에서 {{n}}행을 읽었습니다. 코드 열 "{{code}}", 시즌 열 "{{season}}".',
    statWould: '변경 예정',
    statCorrect: '이미 올바름',
    statNotFound: '코드 없음',
    statBlank: '시즌 비어 있음',
    statMulti: '여러 라인업에 있음',
    statMultiSub: '최신 시즌 유지',
    colSap: 'SAP 코드',
    colNow: '현재 시즌',
    colFile: '파일의 시즌',
    notFound: '여기에 없음, 처음 몇 개: <c>{{list}}</c>',
    multi: '여러 라인업에 있음, 처음 몇 개: <c>{{list}}</c> — 최신 시즌이 기록됩니다.',
    expTitle: 'Style Hub — 스타일 마스터 (SAP 실시간, 시즌 제외)',
    expSheet: '스타일 마스터',
  },
  common: {
    rowNo: '번호',
    exportExcel: '엑셀 내보내기',
    building: '생성 중…',
    clearTools: '정렬 · 필터 해제',
    refresh: '새로고침',
    saving: '저장 중…',
    rowsN: '{{n}}행',
    rowsOf: '{{total}}행 중 {{n}}행',
    search: '검색: {{q}}',
    exportFailed: '내보내기 실패 ({{status}})',
  },
  season: {
    head: '시즌 & Buy#',
    note1: '두 필드 모두 사람이 입력하며, 어떤 가져오기도 이 값을 기록하지 않습니다. 입력은 라인이 아니라 <b>문서</b> 단위입니다: PO 라인은 29,937개지만 구매 오더는 2,738개뿐이며, 오더 하나는 어차피 한 시즌과 한 Buy에 속합니다. 모든 수정은 변경 전후 값과 함께 변경 로그에 기록됩니다.',
    note2: '<b>PLM 시즌</b>은 참고용입니다 — 오더에 있는 스타일에 대해 PLM이 말하는 값이며, 자동으로 기록되지 않습니다. 여기 들어갈 값과 다를 수 있습니다: PO 4500003037은 2026년 8월에 2025 시즌 스타일로 발행되었는데, 재주문은 원래 시즌을 유지하기 때문입니다. 오더의 라인이 둘 이상의 PLM 시즌으로 나뉘면 모두 표시합니다.',
    sapPo: 'SAP PO',
    nsPo: 'NetSuite PO',
    allYears: '전체 연도',
    searchPh: 'PO 번호 또는 공급업체…',
    missing: '시즌 또는 Buy# 누락',
    docs: '문서 {{n}}건',
    seasonPh: '시즌, 예: S26',
    buyPh: 'Buy#, 예: Buy#2 SLT',
    applyTo: '선택한 {{n}}건에 적용',
    colDoc: '문서',
    colDate: '날짜',
    colSupplier: '공급업체',
    colLines: '라인',
    colSeason: '시즌 ✎',
    colBuy: 'Buy# ✎',
    colPlm: 'PLM 시즌',
    editTitle: '클릭하여 편집 — 이 문서의 모든 라인에 적용됩니다',
    mixed: '혼재: {{a}} / {{b}}',
    none: '문서가 없습니다',
    typeFirst: '먼저 시즌 또는 Buy#를 입력하세요',
    done: '문서 {{docs}}건, 라인 {{lines}}개, 변경 로그 {{logged}}건',
  },
  material: {
    head: 'SAP 자재 마스터',
    snapshot: '<c>{{file}}</c>의 스냅샷, {{date}}에 불러옴 — 실시간 SAP 피드가 아닙니다.',
    searchPh: 'SAP 코드, 이름, 레거시 Item#, PLM 번호 또는 바코드',
    allTypes: '전체 유형',
    noMatch: '일치하는 행이 없습니다',
    expTitle: 'Style Hub — SAP 자재 마스터',
    expSheet: '자재 마스터',
    typeFilter: '유형: {{type}}',
    col: {
      sapCode: 'SAP 코드',
      type: '유형',
      nameEn: '이름 (EN)',
      lineCategory: '라인 / 카테고리',
      legacy: '레거시 Item#',
      plm: 'PLM 번호',
      uom: '단위',
      barcode: '바코드',
    },
  },
  ledger: {
    sortAZ: '오름차순 정렬',
    sortZA: '내림차순 정렬',
    searchCol: '{{label}} 검색…',
    loadingValues: '값 불러오는 중…',
    selectAll: '(전체 선택)',
    noMatches: '일치하는 항목 없음',
    truncated: '처음 1,000개 값만 표시합니다 — 검색창으로 좁혀 보세요.',
    clear: '지우기',
    cancel: '취소',
    ok: '확인',
    clickSort: '{{label}} — 클릭하여 정렬',
    filteredTo: '{{n}}개 값으로 필터링됨',
    sortFilter: '정렬 및 필터',
  },
};

const vi = {
  tabs: {
    connections: 'Kết nối',
    style: 'Dữ liệu gốc style',
    material: 'Dữ liệu gốc vật tư',
  },
  state: {
    connected: 'Đã kết nối',
    failed: 'Thất bại',
    notConfigured: 'Chưa cấu hình',
    scheduler: 'Chạy theo lịch',
    upToDate: 'Đã cập nhật',
    lastRunFailed: 'Lần chạy gần nhất thất bại',
    stale: 'Đã cũ',
  },
  conn: {
    transport: 'Phương thức',
    host: 'Máy chủ',
    account: 'Tài khoản',
    roundTrip: 'Thời gian phản hồi',
    waitingFor: 'Đang chờ: {{keys}}',
    scheduled: 'Được làm mới hai lần mỗi ngày bởi tác vụ theo lịch, không phải từ máy chủ này.',
  },
  age: {
    min: '{{n}} phút trước',
    hours: '{{n}} giờ trước',
    days: '{{n}} ngày trước',
  },
  refresh: {
    never: 'chưa bao giờ',
    result: 'đọc {{read}} · +{{inserted}} mới · {{updated}} thay đổi',
    failed: 'thất bại',
    head: 'Làm mới theo lịch',
    sapNow: 'Làm mới SAP ngay',
    nsNow: 'Làm mới NetSuite ngay',
    stepRunning: '{{step}}...',
    checking: 'Đang kiểm tra...',
    checkAgain: 'Kiểm tra lại',
    note: 'Ba sổ cái được tải lại từ SAP và NetSuite theo lịch. Đây là thời điểm mỗi sổ cái được làm mới thành công gần nhất — không phải lúc trang này được mở. Làm mới thủ công làm đúng việc của tác vụ theo lịch; nếu tác vụ đang chạy, hệ thống sẽ báo thay vì chạy song song.',
    done: 'Đã làm mới — {{done}}',
    colLedger: 'Sổ cái',
    colRows: 'Số dòng',
    colLast: 'Làm mới gần nhất',
    colResult: 'Kết quả',
    colState: 'Trạng thái',
    colProblem: 'Sự cố',
    stepSapPo: 'Dòng PO SAP',
    stepSapSo: 'Dòng SO SAP',
    stepNsPo: 'Dòng PO NetSuite',
    needsEdit: 'Làm mới sẽ ghi vào sổ cái nên cần quyền sửa trên Hub.',
    summary: '{{label}}: +{{added}} mới, {{changed}} thay đổi',
  },
  failedStatus: 'Thất bại ({{status}})',
  src: {
    head: 'Kết nối nguồn',
    lastChecked: 'Kiểm tra lần cuối {{time}}',
    testing: 'Đang kiểm tra...',
    testAgain: 'Kiểm tra lại',
    noCreds: 'Máy chủ này không giữ thông tin xác thực tích hợp nào, nên không thể kiểm tra kết nối từ đây — đây là thiết kế có chủ ý, để chỉ một máy giữ thông tin bí mật và chỉ một đường ghi vào sổ cái. Dữ liệu không bị cũ: việc làm mới theo lịch chạy từ backend staging, và bảng bên dưới cho biết lần cuối mỗi sổ cái thành công. Các nút nhập thủ công trên trang này sẽ không hoạt động ở đây.',
    plmNote: '<b>Centric PLM không có trong danh sách.</b> REST API của nó cần bản quyền và đang tắt, nên không có kết nối nào để kiểm tra. Tên style lấy từ SAP (<c>MAKT</c>); Mùa lấy từ file xuất PLM được nhập ở tab <b>Dữ liệu gốc style</b>, và từ giao diện PLM → SAP cho Mùa 2027 trở đi.',
  },
  style: {
    head: 'Dữ liệu nền style',
    note: 'Mọi cột đều lấy từ SAP và dùng đúng tiêu đề cột của SAP. <c>MATNR</c>, <c>FERTH</c>, <c>MAKTX</c>, <c>MTART</c>, <c>MATKL</c>, <c>ZZCOLOR</c> và <c>EAN11</c> được đọc lại qua RFC hai lần mỗi ngày, lúc 05:30 và 13:30. Hai cột <c>PRDHA</c> không nằm trong lịch đó — chúng chỉ được ghi lại khi có người chạy bổ sung dữ liệu style, nên có thể chậm hơn các cột khác. <b>Season</b> là cột duy nhất hoàn toàn không đến từ SAP: nó lấy từ file xuất PLM. <b>Douzone material</b> là tên SAP đặt cho mã vật tư trước thời SAP (Douzone), chính là mã được dùng ở đây làm SKU bán hàng. Tìm kiếm cũng tra cả bảng đối chiếu BOM, nên mã SFP hay mã linh kiện vẫn tìm ra style của nó dù không có cột riêng.',
    noSeason: 'Chưa có mùa',
    countN: '{{n}} style',
    countOf: '{{n}} / {{total}} style',
    noMatch: 'Không có style phù hợp',
    importHead: 'Nhập mùa từ PLM',
    importNote1: 'Dành cho các mùa mà PLM sẽ <b>không</b> gửi qua SAP. Từ Mùa 2027, Mùa sẽ đến qua giao diện SAP (Hyun Kim, 2026-08-26); mọi mùa trước đó đã đóng và giá trị sẽ không bao giờ đổi nữa, nên chỉ cần xuất một lần từ PLM. Thả file xuất đó vào đây — <c>.xlsx</c> hoặc <c>.csv</c>, lấy thẳng từ màn hình danh sách của PLM, không cần chỉnh lại.',
    importNote2: 'Bố cục không quan trọng: cột mã và cột mùa được nhận ra bằng cách xem giá trị, không phải tiêu đề, và bản xem trước sẽ cho biết đã chọn hai cột nào. Mùa trống không bao giờ được ghi, nên một file thiếu style nào đó cũng không thể xóa mùa của style đó.',
    reading: 'Đang đọc...',
    check: 'Kiểm tra file',
    apply_one: 'Áp dụng {{count}} thay đổi',
    apply_other: 'Áp dụng {{count}} thay đổi',
    readError: 'Không đọc được file',
    applied: 'Đã áp dụng — cập nhật {{n}} style từ {{file}}.',
    read: 'Đã đọc {{n}} dòng từ {{file}}. Cột mã "{{code}}", cột mùa "{{season}}".',
    statWould: 'Sẽ thay đổi',
    statCorrect: 'Đã đúng',
    statNotFound: 'Không tìm thấy mã',
    statBlank: 'Mùa trống',
    statMulti: 'Có trong nhiều line-up',
    statMultiSub: 'giữ mùa mới nhất',
    colSap: 'Mã SAP',
    colNow: 'Mùa hiện tại',
    colFile: 'Mùa trong file',
    notFound: 'Không tìm thấy ở đây, vài mã đầu: <c>{{list}}</c>',
    multi: 'Có trong nhiều line-up, vài mã đầu: <c>{{list}}</c> — mùa mới nhất sẽ được ghi.',
    expTitle: 'Style Hub — Dữ liệu gốc style (SAP trực tiếp, trừ Mùa)',
    expSheet: 'Dữ liệu gốc style',
  },
  common: {
    rowNo: 'STT',
    exportExcel: 'Xuất Excel',
    building: 'Đang tạo…',
    clearTools: 'Xóa sắp xếp & bộ lọc',
    refresh: 'Làm mới',
    saving: 'Đang lưu…',
    rowsN: '{{n}} dòng',
    rowsOf: '{{n}} / {{total}} dòng',
    search: 'Tìm: {{q}}',
    exportFailed: 'Xuất thất bại ({{status}})',
  },
  season: {
    head: 'Mùa & Buy#',
    note1: 'Cả hai trường đều do người nhập và không có lần nhập dữ liệu nào ghi vào chúng. Nhập theo <b>chứng từ</b>, không theo dòng: có 29,937 dòng PO nhưng chỉ có 2,738 đơn mua hàng, và một đơn vốn chỉ thuộc một mùa và một đợt Buy. Mọi chỉnh sửa đều được ghi vào nhật ký thay đổi kèm giá trị trước và sau.',
    note2: '<b>Mùa PLM</b> chỉ để tham khảo — đó là điều PLM ghi nhận về các style trong đơn, không bao giờ được ghi tự động. Nó có thể khác một cách hợp lệ với giá trị ở đây: PO 4500003037 được phát hành tháng 8/2026 cho một style Mùa 2025, vì đơn đặt lại giữ mùa gốc. Nếu các dòng của một đơn thuộc nhiều mùa PLM, tất cả đều được hiển thị.',
    sapPo: 'SAP PO',
    nsPo: 'NetSuite PO',
    allYears: 'Tất cả các năm',
    searchPh: 'Số PO hoặc nhà cung cấp…',
    missing: 'Thiếu Mùa hoặc Buy#',
    docs: '{{n}} chứng từ',
    seasonPh: 'Mùa, ví dụ S26',
    buyPh: 'Buy#, ví dụ Buy#2 SLT',
    applyTo: 'Áp dụng cho {{n}} mục đã chọn',
    colDoc: 'Chứng từ',
    colDate: 'Ngày',
    colSupplier: 'Nhà cung cấp',
    colLines: 'Số dòng',
    colSeason: 'Mùa ✎',
    colBuy: 'Buy# ✎',
    colPlm: 'Mùa PLM',
    editTitle: 'Nhấn để sửa — áp dụng cho mọi dòng của chứng từ này',
    mixed: 'không đồng nhất: {{a}} / {{b}}',
    none: 'Không có chứng từ',
    typeFirst: 'Hãy nhập Mùa hoặc Buy# trước',
    done: '{{docs}} chứng từ, {{lines}} dòng, {{logged}} mục nhật ký thay đổi',
  },
  material: {
    head: 'Dữ liệu gốc vật tư SAP',
    snapshot: 'Bản chụp của <c>{{file}}</c>, nạp ngày {{date}} — không phải nguồn SAP trực tiếp.',
    searchPh: 'Mã SAP, tên, Item# cũ, số PLM hoặc mã vạch',
    allTypes: 'Tất cả loại',
    noMatch: 'Không có dòng phù hợp',
    expTitle: 'Style Hub — Dữ liệu gốc vật tư SAP',
    expSheet: 'Dữ liệu gốc vật tư',
    typeFilter: 'Loại: {{type}}',
    col: {
      sapCode: 'Mã SAP',
      type: 'Loại',
      nameEn: 'Tên (EN)',
      lineCategory: 'Dòng / Danh mục',
      legacy: 'Item# cũ',
      plm: 'Số PLM',
      uom: 'ĐVT',
      barcode: 'Mã vạch',
    },
  },
  ledger: {
    sortAZ: 'Sắp xếp từ A đến Z',
    sortZA: 'Sắp xếp từ Z đến A',
    searchCol: 'Tìm {{label}}…',
    loadingValues: 'Đang tải giá trị…',
    selectAll: '(Chọn tất cả)',
    noMatches: 'Không có kết quả',
    truncated: 'Chỉ hiển thị 1,000 giá trị đầu — hãy thu hẹp bằng ô tìm kiếm.',
    clear: 'Xóa lọc',
    cancel: 'Hủy',
    ok: 'Đồng ý',
    clickSort: '{{label}} — nhấn để sắp xếp',
    filteredTo: 'Đã lọc còn {{n}} giá trị',
    sortFilter: 'Sắp xếp và lọc',
  },
};

const zh = {
  tabs: {
    connections: '连接',
    style: '款式主数据',
    material: '物料主数据',
  },
  state: {
    connected: '已连接',
    failed: '失败',
    notConfigured: '未配置',
    scheduler: '由定时任务运行',
    upToDate: '最新',
    lastRunFailed: '上次运行失败',
    stale: '已过期',
  },
  conn: {
    transport: '传输方式',
    host: '主机',
    account: '账户',
    roundTrip: '往返耗时',
    waitingFor: '等待：{{keys}}',
    scheduled: '由定时任务每天刷新两次，而非从此服务器刷新。',
  },
  age: {
    min: '{{n}} 分钟前',
    hours: '{{n}} 小时前',
    days: '{{n}} 天前',
  },
  refresh: {
    never: '从未',
    result: '读取 {{read}} · 新增 +{{inserted}} · 更改 {{updated}}',
    failed: '失败',
    head: '定时刷新',
    sapNow: '立即刷新 SAP',
    nsNow: '立即刷新 NetSuite',
    stepRunning: '{{step}}...',
    checking: '检查中...',
    checkAgain: '重新检查',
    note: '三个台账按计划从 SAP 和 NetSuite 重新加载。这里显示的是每个台账最近一次成功的时间——而不是打开此页面的时间。手动刷新与定时任务执行相同的工作；如果任务正在运行，会提示您，而不会同时运行。',
    done: '已刷新 — {{done}}',
    colLedger: '台账',
    colRows: '行数',
    colLast: '最近刷新',
    colResult: '结果',
    colState: '状态',
    colProblem: '问题',
    stepSapPo: 'SAP PO 行',
    stepSapSo: 'SAP SO 行',
    stepNsPo: 'NetSuite PO 行',
    needsEdit: '刷新会写入台账，需要 Hub 的编辑权限。',
    summary: '{{label}}：新增 +{{added}}，更改 {{changed}}',
  },
  failedStatus: '失败 ({{status}})',
  src: {
    head: '数据源连接',
    lastChecked: '上次检查 {{time}}',
    testing: '测试中...',
    testAgain: '重新测试',
    noCreds: '此服务器未保存任何集成凭据，因此无法从这里测试连接——这是有意的设计：只由一台机器保存密钥，只由一条路径写入台账。数据并未过期：定时刷新在 staging 后端运行，下方表格显示每个台账最近一次成功的时间。本页的手动导入按钮在这里无法使用。',
    plmNote: '<b>Centric PLM 不在列表中。</b>其 REST API 需要授权且已关闭，因此没有可测试的连接。款式名称来自 SAP（<c>MAKT</c>）；季节来自在<b>款式主数据</b>标签页导入的 PLM 导出文件，2027 季起来自 PLM → SAP 接口。',
  },
  style: {
    head: '款式基础数据',
    note: '每一列都来自 SAP，并使用 SAP 自己的列标题。<c>MATNR</c>、<c>FERTH</c>、<c>MAKTX</c>、<c>MTART</c>、<c>MATKL</c>、<c>ZZCOLOR</c> 和 <c>EAN11</c> 每天通过 RFC 重新读取两次，时间为 05:30 和 13:30。两个 <c>PRDHA</c> 列不在该计划内——只有在有人运行款式补充时才会重写，因此可能落后于其他列。<b>Season</b> 是唯一完全不来自 SAP 的列：它来自 PLM 导出。<b>Douzone material</b> 是 SAP 对 SAP 之前（Douzone）物料编号的称呼，此处用作销售 SKU。搜索还会查到 BOM 对照表，因此即使 SFP 或部件代码没有自己的列，也能找到其款式。',
    noSeason: '尚无季节',
    countN: '{{n}} 个款式',
    countOf: '{{n}} / {{total}} 个款式',
    noMatch: '没有匹配的款式',
    importHead: '导入 PLM 季节',
    importNote1: '用于 PLM <b>不会</b>通过 SAP 发送的季节。从 2027 季起，季节通过 SAP 接口传入（Hyun Kim，2026-08-26）；更早的都是已结束的季节，其值不会再变，因此从 PLM 导出一次即可。把该导出文件放在这里——直接从 PLM 列表视图导出的 <c>.xlsx</c> 或 <c>.csv</c>，无需调整格式。',
    importNote2: '布局无关紧要：代码列和季节列是通过查看值而不是表头来识别的，预览会显示选中了哪两列。空白季节永远不会被写入，因此缺少某个款式的文件不会清除其季节。',
    reading: '读取中...',
    check: '检查文件',
    apply_one: '应用 {{count}} 项更改',
    apply_other: '应用 {{count}} 项更改',
    readError: '无法读取文件',
    applied: '已应用 — 已从 {{file}} 更新 {{n}} 个款式。',
    read: '已从 {{file}} 读取 {{n}} 行。代码列“{{code}}”，季节列“{{season}}”。',
    statWould: '将更改',
    statCorrect: '已正确',
    statNotFound: '未找到代码',
    statBlank: '季节为空',
    statMulti: '属于多个系列',
    statMultiSub: '保留最新季节',
    colSap: 'SAP 代码',
    colNow: '当前季节',
    colFile: '文件中的季节',
    notFound: '此处未找到，前几个：<c>{{list}}</c>',
    multi: '属于多个系列，前几个：<c>{{list}}</c> — 写入的是最新季节。',
    expTitle: 'Style Hub — 款式主数据（SAP 实时，季节除外）',
    expSheet: '款式主数据',
  },
  common: {
    rowNo: '序号',
    exportExcel: '导出 Excel',
    building: '生成中…',
    clearTools: '清除排序和筛选',
    refresh: '刷新',
    saving: '保存中…',
    rowsN: '{{n}} 行',
    rowsOf: '{{n}} / {{total}} 行',
    search: '搜索：{{q}}',
    exportFailed: '导出失败 ({{status}})',
  },
  season: {
    head: '季节 & Buy#',
    note1: '两个字段都由人工填写，任何导入都不会写入。按<b>单据</b>而非按行录入：PO 行有 29,937 行，而采购订单只有 2,738 张，而且一张订单本来就只属于一个季节和一次 Buy。每次修改都会连同修改前后的值记录在变更日志中。',
    note2: '<b>PLM 季节</b>仅供参考——是 PLM 对订单上款式的说法，从不自动写入。它可能与这里应填的值合理地不同：PO 4500003037 于 2026 年 8 月为一个 2025 季款式下单，因为补单会保留原季节。如果一张订单的各行对应多个 PLM 季节，会全部显示。',
    sapPo: 'SAP PO',
    nsPo: 'NetSuite PO',
    allYears: '所有年份',
    searchPh: 'PO 号或供应商…',
    missing: '缺少季节或 Buy#',
    docs: '{{n}} 张单据',
    seasonPh: '季节，例如 S26',
    buyPh: 'Buy#，例如 Buy#2 SLT',
    applyTo: '应用到所选的 {{n}} 项',
    colDoc: '单据',
    colDate: '日期',
    colSupplier: '供应商',
    colLines: '行数',
    colSeason: '季节 ✎',
    colBuy: 'Buy# ✎',
    colPlm: 'PLM 季节',
    editTitle: '点击编辑——应用于此单据的所有行',
    mixed: '不一致：{{a}} / {{b}}',
    none: '没有单据',
    typeFirst: '请先输入季节或 Buy#',
    done: '{{docs}} 张单据，{{lines}} 行，{{logged}} 条变更日志',
  },
  material: {
    head: 'SAP 物料主数据',
    snapshot: '<c>{{file}}</c> 的快照，加载于 {{date}}——不是实时 SAP 数据。',
    searchPh: 'SAP 代码、名称、旧 Item#、PLM 编号或条码',
    allTypes: '所有类型',
    noMatch: '没有匹配的行',
    expTitle: 'Style Hub — SAP 物料主数据',
    expSheet: '物料主数据',
    typeFilter: '类型：{{type}}',
    col: {
      sapCode: 'SAP 代码',
      type: '类型',
      nameEn: '名称 (EN)',
      lineCategory: '产品线 / 类别',
      legacy: '旧 Item#',
      plm: 'PLM 编号',
      uom: '单位',
      barcode: '条码',
    },
  },
  ledger: {
    sortAZ: '按 A 到 Z 排序',
    sortZA: '按 Z 到 A 排序',
    searchCol: '搜索{{label}}…',
    loadingValues: '正在加载值…',
    selectAll: '（全选）',
    noMatches: '无匹配项',
    truncated: '仅显示前 1,000 个值——请用搜索框缩小范围。',
    clear: '清除',
    cancel: '取消',
    ok: '确定',
    clickSort: '{{label}} — 点击排序',
    filteredTo: '已筛选为 {{n}} 个值',
    sortFilter: '排序和筛选',
  },
};

const dictionary = { ns: 'xHub', en, ko, vi, zh };

export default dictionary;
